import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  CCard,
  CCardBody,
  CCol,
  CRow,
  CButton,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CFormGroup,
  CLabel,
  CInput,
  CTextarea,
} from '@coreui/react';

// --- Assume you have these actions defined in your Redux actions files ---
// import { updateUserProfile, changeUserPassword } from '../../actions/profileActions';

const Profile = () => {
  const dispatch = useDispatch();

  // 1. Get user data from Redux store
  const user = useSelector(state => state.loginReducer);

  // 2. Manage UI state (active tab, modal visibility)
  const [activeTab, setActiveTab] = useState('about');
  const [editModalVisible, setEditModalVisible] = useState(false);

  // 3. Manage form data with local state
  const [profileData, setProfileData] = useState({
    name: '',
    dob: '',
    email: '',
    mobile: '',
    address: '',
    about: '',
    profileImageSrc: 'admin-assets/img/profiles/avatar-01.jpg',
  });

  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // 4. Populate local state when component loads or user data changes
  useEffect(() => {
    if (user) {
      setProfileData({
        // Use data from Redux, with fallbacks for safety
        name: user.StaffName || 'N/A',
        dob: user.dob || '24 Jul 1983', // Example: Add 'dob' to your Redux state
        email: user.email || 'N/A',
        mobile: user.mobile || 'N/A',
        address: user.address || '4663 Agriculture Lane,\nMiami,\nFlorida - 33165,\nUnited States.', // Example
        about: user.about || 'Lorem ipsum dolor sit amet...', // Example
        profileImageSrc: user.profileimage || 'admin-assets/img/profiles/avatar-01.jpg',
      });
    }
  }, [user]); // This effect runs when the 'user' object from Redux changes

  // 5. Handlers for form input changes
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  // 6. Handlers for form submissions
  const handleProfileSubmit = (e) => {
    e.preventDefault();
    console.log('Dispatching update for:', profileData);
    // dispatch(updateUserProfile(profileData)); // <-- UNCOMMENT to dispatch your action
    setEditModalVisible(false); // Close modal on save
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert("New passwords do not match!");
      return;
    }
    console.log('Dispatching password change...');
    // dispatch(changeUserPassword(passwordData)); // <-- UNCOMMENT to dispatch your action
    setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' }); // Clear fields
  };

  return (
    // This component assumes it's rendered within a layout that has a header and sidebar.
    // The main-wrapper and page-wrapper should be in your layout component.
    <div className="content container-fluid pt-0">
      {/* Page Header */}
      <div className="page-header">
        <div className="row">
          <div className="col">
            <h3 className="page-title">Profile</h3>
            <ul className="breadcrumb">
              <li className="breadcrumb-item"><Link to="/admin/dashboard">Dashboard</Link></li>
              <li className="breadcrumb-item active">Profile</li>
            </ul>
          </div>
        </div>
      </div>
      {/* /Page Header */}
      <CRow>
        <CCol md="12">
          <div className="profile-header">
            <div className="row align-items-center">
              <div className="col-auto profile-image">
                <a href="#">
                  <img className="rounded-circle" alt="User" src={profileData.profileImageSrc} />
                </a>
              </div>
              <div className="col ml-md-n2 profile-user-info">
                <h4 className="user-name mb-0">{profileData.name}</h4>
                <h6 className="text-muted">{profileData.email}</h6>
                <div className="about-text">{profileData.about}</div>
              </div>
              <div className="col-auto profile-btn">
                <CButton color="primary" onClick={() => setEditModalVisible(true)}>
                  Edit
                </CButton>
              </div>
            </div>
          </div>
          <div className="profile-menu">
            <ul className="nav nav-tabs nav-tabs-solid">
              <li className="nav-item">
                <a
                  className={`nav-link ${activeTab === 'about' ? 'active' : ''}`}
                  href="#per_details_tab"
                  onClick={(e) => { e.preventDefault(); setActiveTab('about'); }}
                >
                  About
                </a>
              </li>
              <li className="nav-item">
                <a
                  className={`nav-link ${activeTab === 'password' ? 'active' : ''}`}
                  href="#password_tab"
                  onClick={(e) => { e.preventDefault(); setActiveTab('password'); }}
                >
                  Password
                </a>
              </li>
            </ul>
          </div>
          <div className="tab-content profile-tab-cont">
            {/* Personal Details Tab */}
            {activeTab === 'about' && (
              <div className="tab-pane fade show active" id="per_details_tab">
                <CCard>
                  <CCardBody>
                    <h5 className="card-title d-flex justify-content-between">
                      <span>Personal Details</span>
                      <a
                        className="edit-link"
                        href="#"
                        onClick={(e) => { e.preventDefault(); setEditModalVisible(true); }}
                      >
                        <i className="fa fa-edit mr-1" />Edit
                      </a>
                    </h5>
                    <div className="row">
                      <p className="col-sm-2 text-muted text-sm-right mb-0 mb-sm-3">Name</p>
                      <p className="col-sm-10">{profileData.name}</p>
                    </div>
                    <div className="row">
                      <p className="col-sm-2 text-muted text-sm-right mb-0 mb-sm-3">Date of Birth</p>
                      <p className="col-sm-10">{profileData.dob}</p>
                    </div>
                    <div className="row">
                      <p className="col-sm-2 text-muted text-sm-right mb-0 mb-sm-3">Email ID</p>
                      <p className="col-sm-10">{profileData.email}</p>
                    </div>
                    <div className="row">
                      <p className="col-sm-2 text-muted text-sm-right mb-0 mb-sm-3">Mobile</p>
                      <p className="col-sm-10">{profileData.mobile}</p>
                    </div>
                    <div className="row">
                      <p className="col-sm-2 text-muted text-sm-right mb-0">Address</p>
                      <p className="col-sm-10 mb-0" style={{ whiteSpace: 'pre-line' }}>
                        {profileData.address}
                      </p>
                    </div>
                  </CCardBody>
                </CCard>
              </div>
            )}
            {/* /Personal Details Tab */}

            {/* Change Password Tab */}
            {activeTab === 'password' && (
              <div className="tab-pane fade show active" id="password_tab">
                <CCard>
                  <CCardBody>
                    <h5 className="card-title">Change Password</h5>
                    <CRow>
                      <CCol md="10" lg="6">
                        <form onSubmit={handlePasswordSubmit}>
                          <CFormGroup>
                            <CLabel>Old Password</CLabel>
                            <CInput type="password" name="oldPassword" value={passwordData.oldPassword} onChange={handlePasswordChange} required />
                          </CFormGroup>
                          <CFormGroup>
                            <CLabel>New Password</CLabel>
                            <CInput type="password" name="newPassword" value={passwordData.newPassword} onChange={handlePasswordChange} required />
                          </CFormGroup>
                          <CFormGroup>
                            <CLabel>Confirm Password</CLabel>
                            <CInput type="password" name="confirmPassword" value={passwordData.confirmPassword} onChange={handlePasswordChange} required />
                          </CFormGroup>
                          <CButton color="primary" type="submit">Save Changes</CButton>
                        </form>
                      </CCol>
                    </CRow>
                  </CCardBody>
                </CCard>
              </div>
            )}
            {/* /Change Password Tab */}
          </div>
        </CCol>
      </CRow>

      {/* Edit Details Modal */}
      <CModal show={editModalVisible} onClose={() => setEditModalVisible(false)} centered>
        <CModalHeader closeButton>
          <CModalTitle>Personal Details</CModalTitle>
        </CModalHeader>
        <form onSubmit={handleProfileSubmit}>
          <CModalBody>
            <CRow>
              <CCol xs="12">
                <CFormGroup>
                  <CLabel>Name</CLabel>
                  <CInput name="name" value={profileData.name} onChange={handleProfileChange} />
                </CFormGroup>
              </CCol>
              <CCol xs="12">
                <CFormGroup>
                  <CLabel>Date of Birth</CLabel>
                  <CInput type="date" name="dob" value={profileData.dob} onChange={handleProfileChange} />
                </CFormGroup>
              </CCol>
              <CCol xs="12">
                <CFormGroup>
                  <CLabel>Email ID</CLabel>
                  <CInput type="email" name="email" value={profileData.email} onChange={handleProfileChange} />
                </CFormGroup>
              </CCol>
              <CCol xs="12">
                <CFormGroup>
                  <CLabel>Mobile</CLabel>
                  <CInput name="mobile" value={profileData.mobile} onChange={handleProfileChange} />
                </CFormGroup>
              </CCol>
              <CCol xs="12">
                <CFormGroup>
                  <CLabel>Address</CLabel>
                  <CTextarea name="address" rows="4" value={profileData.address} onChange={handleProfileChange} />
                </CFormGroup>
              </CCol>
            </CRow>
          </CModalBody>
          <CModalFooter>
            <CButton color="secondary" onClick={() => setEditModalVisible(false)}>Cancel</CButton>
            <CButton color="primary" type="submit">Save Changes</CButton>
          </CModalFooter>
        </form>
      </CModal>
    </div>
  );
};

// No need for connect() or mapStateToProps when using hooks
export default Profile;